import { useEffect, useMemo, useState } from 'react';
import Map, { Layer, Marker, Source } from 'react-map-gl/mapbox';
import circle from '@turf/circle';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPin } from 'lucide-react';

import { geocodeQuery } from '@/lib/geocode';

// Same Mapbox style + public token used by `@nearst/periscope-ui` and the
// retailers-dashboard so all NearSt-branded maps render identically.
const MAPBOX_STYLE = 'mapbox://styles/devnearst/cm365buc601b901pi1hcv24j9';
const MAPBOX_TOKEN = 'pk.eyJ1IjoiZGV2bmVhcnN0IiwiYSI6ImNseDRqYXJvOTE2Mm0ydnM0YnQxemEzajYifQ.pCibszFZL8Pi7k7OswvrpA';

const FALLBACK_CENTRE = { lat: 51.5074, lng: -0.1278 };

function parseGeolocation(raw) {
	if (!raw) return null;
	try {
		const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
		const lat = Number(parsed?.lat);
		const lng = Number(parsed?.lng);
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
		return { lat, lng };
	} catch {
		return null;
	}
}

export function ShopMap({ shop, label, radiusKm }) {
	const displayLabel = label || shop?.city || 'Your store';

	// Resolve the map centre by geocoding the shop's address rather than reading
	// `shop.geolocation`/`googlePlaceId`, both of which can carry the leaked
	// London registration default (NEA-5848). Fall back to the stored
	// geolocation, then London, if the geocode finds nothing. `null` while the
	// lookup is in flight so the map mounts with the right `initialViewState`.
	const [centre, setCentre] = useState(null);
	useEffect(() => {
		let cancelled = false;
		const query = [shop?.name, shop?.address, shop?.postcode, shop?.city].filter(Boolean).join(', ');
		const fallback = () => parseGeolocation(shop?.geolocation) || FALLBACK_CENTRE;
		(async () => {
			try {
				const coords = await geocodeQuery(query);
				if (!cancelled) setCentre(coords || fallback());
			} catch {
				if (!cancelled) setCentre(fallback());
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [shop?.id, shop?.name, shop?.address, shop?.postcode, shop?.city, shop?.geolocation]);

	// Mirror retailers-dashboard `AdsRadius`: a semi-transparent indigo disc
	// around the shop pin marking the ad-targeting radius. Built as a GeoJSON
	// circle via @turf/circle so Mapbox can render it at correct earth-scale
	// (not pixel-scale) and it stays geographically accurate at any zoom.
	const radiusGeoJson = useMemo(() => {
		if (!centre || !radiusKm || !Number.isFinite(radiusKm)) return null;
		return {
			type: 'FeatureCollection',
			features: [circle([centre.lng, centre.lat], radiusKm)]
		};
	}, [centre, radiusKm]);

	// When a radius is set, compute the geographic bounds enclosing the disc
	// and let Mapbox fit to them. This guarantees the full circle is visible
	// regardless of viewport aspect ratio (zoom heuristics break at varying
	// card widths). When no radius, fall back to a fixed shop-centred zoom.
	const initialViewState = useMemo(() => {
		if (!centre) return null;
		if (!radiusKm || !Number.isFinite(radiusKm)) {
			return { longitude: centre.lng, latitude: centre.lat, zoom: 12 };
		}
		const radiusDeg = radiusKm / 111;
		const lngRadius = radiusDeg / Math.cos((centre.lat * Math.PI) / 180);
		const bounds = [
			[centre.lng - lngRadius, centre.lat - radiusDeg],
			[centre.lng + lngRadius, centre.lat + radiusDeg]
		];
		return { bounds, fitBoundsOptions: { padding: 28 } };
	}, [centre, radiusKm]);

	return (
		<div className="relative h-full min-h-[260px] w-full overflow-hidden [&_.mapboxgl-ctrl-bottom-left]:hidden [&_.mapboxgl-ctrl-bottom-right]:hidden">
			{centre ? (
				<Map
					mapboxAccessToken={MAPBOX_TOKEN}
					mapStyle={MAPBOX_STYLE}
					initialViewState={initialViewState}
					attributionControl={false}
					dragPan={false}
					scrollZoom={false}
					doubleClickZoom={false}
					dragRotate={false}
					touchPitch={false}
					touchZoomRotate={false}
					style={{ width: '100%', height: '100%' }}
				>
					{radiusGeoJson ? (
						<Source id="ads-radius" type="geojson" data={radiusGeoJson}>
							<Layer
								id="ads-radius-fill"
								type="fill"
								paint={{
									'fill-color': '#4F46E5',
									'fill-opacity': 0.28,
									'fill-antialias': true
								}}
							/>
							<Layer
								id="ads-radius-line"
								type="line"
								paint={{
									'line-color': '#4F46E5',
									'line-width': 1.5,
									'line-opacity': 0.7
								}}
							/>
						</Source>
					) : null}
					<Marker longitude={centre.lng} latitude={centre.lat} anchor="center">
						<div className="flex h-10 w-10 items-center justify-center rounded-full bg-foreground text-background shadow-md ring-2 ring-background">
							<MapPin className="h-5 w-5" />
						</div>
					</Marker>
				</Map>
			) : (
				<div className="h-full w-full animate-pulse bg-muted" />
			)}
			<div className="pointer-events-none absolute left-3 top-3 rounded-full bg-foreground/80 px-3 py-1 text-xs font-semibold text-background shadow-sm">
				{displayLabel}
			</div>
			{radiusKm ? (
				<div className="pointer-events-none absolute right-3 top-3 rounded-full bg-foreground/80 px-3 py-1 text-xs font-semibold text-background shadow-sm">
					{radiusKm}km radius
				</div>
			) : null}
		</div>
	);
}
